import { signupTenant } from '@/lib/api';
import type { SignupTenantInput } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Loader2, PawPrint } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from './useAuthContext';

// ── Curated options ───────────────────────────────────────────────────────────

const CURRENCIES = [
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
] as const;

const TIMEZONES = [
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Europe/Dublin (GMT/IST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'America/New_York (ET)' },
  { value: 'America/Chicago', label: 'America/Chicago (CT)' },
  { value: 'America/Denver', label: 'America/Denver (MT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PT)' },
  { value: 'America/Toronto', label: 'America/Toronto (ET)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (PT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
] as const;

const LOCALES = [
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-IE', label: 'English (Ireland)' },
] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function passwordStrength(pw: string): { label: string; colour: string } {
  if (pw.length === 0) return { label: '', colour: '' };
  if (pw.length < 10) return { label: 'Too short (min 10)', colour: 'text-destructive' };
  if (pw.length < 14) return { label: 'Fair', colour: 'text-amber-600' };
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  const score = [hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (score >= 2) return { label: 'Strong', colour: 'text-success' };
  return { label: 'Good', colour: 'text-success' };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: children always contains an input; biome can't introspect ReactNode
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50';

const selectCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50';

// ── Main component ────────────────────────────────────────────────────────────

export function SignupPage() {
  const { refresh } = useAuthContext();
  const navigate = useNavigate();

  // Account fields
  const [accountName, setAccountName] = useState('');
  const [currency, setCurrency] = useState<SignupTenantInput['currency']>('GBP');
  const [defaultTimezone, setDefaultTimezone] = useState('Europe/London');
  const [locale, setLocale] = useState('en-GB');

  // Facility
  const [firstFacilityName, setFirstFacilityName] = useState('');

  // Owner
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');

  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);

  const pwStrength = passwordStrength(ownerPassword);

  const handleSubmit = async () => {
    setFieldErrors({});
    setGlobalError(null);
    setBusy(true);

    const accountSlug = slugify(accountName);
    const body: SignupTenantInput = {
      accountName,
      currency,
      defaultTimezone,
      locale,
      ownerEmail,
      ownerPassword,
      ownerName,
      firstFacilityName,
      ...(accountSlug ? { accountSlug } : {}),
    };

    try {
      await signupTenant(body);
      await refresh();
      navigate('/onboarding', { replace: true });
    } catch (err: unknown) {
      const apiErr = err as { status?: number; code?: string; body?: Record<string, unknown> };
      if (apiErr.status === 409) {
        if (apiErr.code === 'email_in_use') {
          setFieldErrors({
            ownerEmail: 'An account already exists for this email — sign in instead.',
          });
        } else {
          setFieldErrors({
            accountName: 'This business name is already taken. Try a different name.',
          });
        }
      } else if (apiErr.status === 400) {
        const issues = (apiErr.body?.issues as string[] | undefined) ?? [];
        const newErrors: Record<string, string> = {};
        for (const issue of issues) {
          if (issue.includes('accountName')) newErrors.accountName = issue;
          else if (issue.includes('currency')) newErrors.currency = issue;
          else if (issue.includes('defaultTimezone')) newErrors.defaultTimezone = issue;
          else if (issue.includes('locale')) newErrors.locale = issue;
          else if (issue.includes('ownerEmail')) newErrors.ownerEmail = issue;
          else if (issue.includes('ownerPassword')) newErrors.ownerPassword = issue;
          else if (issue.includes('ownerName')) newErrors.ownerName = issue;
          else if (issue.includes('firstFacilityName')) newErrors.firstFacilityName = issue;
          else newErrors._global = issue;
        }
        if (newErrors._global) setGlobalError(newErrors._global);
        setFieldErrors(newErrors);
      } else {
        setGlobalError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const canSubmit =
    !busy &&
    accountName.trim().length > 0 &&
    firstFacilityName.trim().length > 0 &&
    ownerName.trim().length > 0 &&
    ownerEmail.includes('@') &&
    ownerPassword.length >= 10;

  return (
    <div className="grid min-h-[100dvh] place-items-center bg-background px-6 py-12">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-primary text-primary-foreground elevate">
            <PawPrint className="size-5" strokeWidth={1.75} />
          </div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Kennel Desk
          </p>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Set up your kennel</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Takes about two minutes. No credit card required.
          </p>
        </div>

        <form
          className="rounded-2xl bg-surface ring-1 ring-border elevate p-6 md:p-8 space-y-8"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {/* Section: Account */}
          <section>
            <h2 className="mb-4 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Your business
            </h2>
            <div className="space-y-4">
              <Field label="Business name">
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. Riverside Kennels"
                  autoComplete="organization"
                  disabled={busy}
                  className={inputCls}
                />
                {fieldErrors.accountName && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.accountName}</p>
                )}
              </Field>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="Currency">
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value as SignupTenantInput['currency'])}
                    disabled={busy}
                    className={selectCls}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.currency && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.currency}</p>
                  )}
                </Field>

                <Field label="Locale">
                  <select
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    disabled={busy}
                    className={selectCls}
                  >
                    {LOCALES.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.locale && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.locale}</p>
                  )}
                </Field>

                <Field label="Timezone">
                  <select
                    value={defaultTimezone}
                    onChange={(e) => setDefaultTimezone(e.target.value)}
                    disabled={busy}
                    className={selectCls}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.defaultTimezone && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.defaultTimezone}</p>
                  )}
                </Field>
              </div>
            </div>
          </section>

          {/* Divider */}
          <hr className="border-border" />

          {/* Section: First facility */}
          <section>
            <h2 className="mb-4 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              First facility
            </h2>
            <Field label="Facility name">
              <input
                type="text"
                value={firstFacilityName}
                onChange={(e) => setFirstFacilityName(e.target.value)}
                placeholder="e.g. Glasgow site"
                disabled={busy}
                className={inputCls}
              />
              {fieldErrors.firstFacilityName && (
                <p className="mt-1 text-xs text-destructive">{fieldErrors.firstFacilityName}</p>
              )}
            </Field>
          </section>

          {/* Divider */}
          <hr className="border-border" />

          {/* Section: Owner account */}
          <section>
            <h2 className="mb-4 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Your account
            </h2>
            <div className="space-y-4">
              <Field label="Full name">
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  autoComplete="name"
                  disabled={busy}
                  className={inputCls}
                />
                {fieldErrors.ownerName && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.ownerName}</p>
                )}
              </Field>

              <Field label="Email">
                <input
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  autoComplete="email"
                  disabled={busy}
                  className={inputCls}
                />
                {fieldErrors.ownerEmail && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.ownerEmail}</p>
                )}
              </Field>

              <Field label="Password">
                <input
                  type="password"
                  value={ownerPassword}
                  onChange={(e) => setOwnerPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={busy}
                  className={inputCls}
                />
                {ownerPassword.length > 0 && (
                  <p className={cn('mt-1 text-xs font-medium', pwStrength.colour)}>
                    {pwStrength.label}
                  </p>
                )}
                {fieldErrors.ownerPassword && (
                  <p className="mt-1 text-xs text-destructive">{fieldErrors.ownerPassword}</p>
                )}
              </Field>
            </div>
          </section>

          {/* Global error */}
          {globalError && <p className="text-sm text-destructive">{globalError}</p>}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground',
              'px-4 py-2.5 text-sm font-medium press disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {busy && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
            Create my kennel
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary underline underline-offset-4">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
